import { useEffect, useState } from "react";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";

import { AccountCard } from "../components/profile/AccountCard";
import { ApiKey } from "../redux/reducers/userSlice";
import { ApiKeyList } from "../components/profile/ApiKeyList";
import { Button, Text, View, Container, Card } from "../components/base";
import type { TextProps } from "@voicerun/design-system";
import { Dialog } from "../components/base/Dialog";
import { Icon } from "../components/base/Icon";
import { InputField } from "../components/base/InputField";
import { PhoneNumberCard } from "../components/profile/PhoneNumberCard";
import { useApiRequest } from "../hooks/useApiRequest";
import { useAppSelector } from "../redux";

type Tab = {
  id: string;
  label: string;
  icon: string;
};

const baseTabs: Tab[] = [
  { id: "overview", label: "Overview", icon: "home" },
  { id: "api-keys", label: "API Keys", icon: "key" },
];

export const ProfilePage = () => {
  const user = useAppSelector((state) => state.session.user);
  const navigate = useNavigate();

  const [showDescriptionDialog, setShowDescriptionDialog] = useState(false);
  const [description, setDescription] = useState("");
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
  const [newApiKey, setNewApiKey] = useState("");
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const createApiKeyRequest = useApiRequest<{ data: ApiKey }>();
  const getApiKeysRequest = useApiRequest<{ data: ApiKey[] }>();
  const revokeApiKeyRequest = useApiRequest();

  const tabs = baseTabs;

  const [activeTab, setActiveTab] = useState(() => {
    // Initialize from URL hash or default to 'overview'
    const hash = window.location.hash.slice(1);
    return tabs.some((tab) => tab.id === hash) ? hash : "overview";
  });

  // Update URL hash when active tab changes
  useEffect(() => {
    window.location.hash = activeTab;
  }, [activeTab]);

  // Listen for hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (tabs.some((tab) => tab.id === hash)) {
        setActiveTab(hash);
      }
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const onClickCreateApiKey = async () => {
    const response = await createApiKeyRequest.fetch("/v1/users/apiKeys", {
      method: "POST",
      body: { description },
    });

    if (response?.data) {
      setNewApiKey(response.data?.data?.token || "");
      setShowApiKeyDialog(true);
      setShowDescriptionDialog(false);
      setDescription("");
      getApiKeysRequest.fetch("/v1/users/apiKeys");
    }
  };

  const onClickDeleteApiKey = async (keyId: string) => {
    await revokeApiKeyRequest.fetch(`/v1/users/apiKeys/${keyId}`, {
      method: "DELETE",
    });
    getApiKeysRequest.fetch("/v1/users/apiKeys");
  };

  const onClickCopyApiKey = async () => {
    await navigator.clipboard.writeText(newApiKey);
    setShowCopySuccess(true);
    setTimeout(() => setShowCopySuccess(false), 1500);
  };

  useEffect(() => {
    getApiKeysRequest.fetch("/v1/users/apiKeys");
  }, []);

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <View className="space-y-4 sm:space-y-6">
            <AccountCard email={user?.email} />
            <PhoneNumberCard phoneNumberId={user?.phoneNumberId} />
            {user?.isAdmin && (
              <Card className="p-6 relative">
                <View className="sm:pr-32">
                  <Text className="text-lg font-semibold mb-4 mr-1">Notification Preferences</Text>
                  <Text className="text-sm text-gray-500 dark:text-gray-400">Manage your notification settings and preferences</Text>
                </View>
                <View className="mt-4 sm:mt-0 sm:absolute sm:right-6 sm:top-1/2 sm:-translate-y-1/2">
                  <Button
                    onClick={() => navigate("/notification-preferences")}
                    variant="brand"
                    className="w-full sm:w-auto"
                  >
                    Manage
                  </Button>
                </View>
              </Card>
            )}
          </View>
        );
      case "api-keys":
        return (
          <ApiKeyList
            apiKeys={getApiKeysRequest.data?.data || []}
            onCreateKey={() => setShowDescriptionDialog(true)}
            onDeleteKey={onClickDeleteApiKey}
            isLoading={getApiKeysRequest.isLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="w-full min-h-full bg-white dark:bg-slate-900">
      <Container
        size="xl"
        className="py-8 min-h-full"
      >
        {/* Mobile Tab Menu */}
        <div className="sm:hidden mb-6">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="flex items-center justify-between w-full p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
          >
            <div className="flex items-center">
              <Icon
                name={tabs.find((tab) => tab.id === activeTab)?.icon || "home"}
                size={20}
                className="mr-3"
              />
              <Text className="font-medium">{tabs.find((tab) => tab.id === activeTab)?.label || "Overview"}</Text>
            </div>
            <Icon
              name={isMobileMenuOpen ? "chevron-up" : "chevron-down"}
              size={20}
            />
          </button>

          {isMobileMenuOpen && (
            <div className="mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setIsMobileMenuOpen(false);
                  }}
                  className={clsx(
                    "w-full flex items-center px-3 py-3 text-left border-b border-gray-100 dark:border-gray-700 last:border-b-0",
                    activeTab === tab.id ? "bg-brand/10 text-brand" : "hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300",
                  )}
                >
                  <Icon
                    name={tab.icon}
                    size={20}
                    className="mr-3"
                  />
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Desktop Tab Navigation */}
        <div className="hidden sm:block border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  "flex items-center px-1 py-4 text-sm font-medium border-b-2 whitespace-nowrap",
                  activeTab === tab.id
                    ? "border-brand text-brand dark:text-brand-light"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300",
                )}
              >
                <Icon
                  name={tab.icon}
                  size={20}
                  className="mr-2"
                />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
        {/* Main Content */}
        <div>{renderTabContent()}</div>
      </Container>

      <Dialog
        isOpen={showDescriptionDialog}
        onClose={() => setShowDescriptionDialog(false)}
        title="New API Key"
      >
        <View className="p-4 space-y-4">
          <Text variant="body">Enter a description for your new API key:</Text>
          <InputField
            value={description}
            onChange={(description) => setDescription(description)}
            placeholder="Enter key description..."
            className="w-full text-slate-700"
          />
          <Button
            onClick={onClickCreateApiKey}
            isDisabled={!description || createApiKeyRequest.isLoading}
            className="bg-brand hover:bg-brand-dark disabled:bg-brand/50 text-white px-4 py-2 w-full"
          >
            {createApiKeyRequest.isLoading ? "Generating..." : "Generate API Key"}
          </Button>
        </View>
      </Dialog>

      <Dialog
        isOpen={showApiKeyDialog}
        onClose={() => setShowApiKeyDialog(false)}
        title="Your New API Key"
      >
        <View className="flex flex-col p-4">
          <Text
            variant="body"
            className="mb-4"
          >
            Please save this API key - you won't be able to see it again!
          </Text>
          {/* <Text
            variant="body"
            className="mb-4"
          >
            You can view documentation for the API{" "}
            <a
              href={`${API_URL}/docs`}
              target="_blank"
              className="text-brand hover:text-brand-light transition-colors"
            >
              here
            </a>
            .
          </Text> */}
          <View className="flex items-center space-x-2">
            <InputField
              value={newApiKey}
              readOnly
              className="flex-1"
            />
            <Button
              onClick={onClickCopyApiKey}
              variant="brand"
              className="whitespace-nowrap"
            >
              {showCopySuccess ? "Copied!" : "Copy Key"}
            </Button>
          </View>
        </View>
      </Dialog>
    </div>
  );
};
